/* eslint-disable @typescript-eslint/no-non-null-assertion,@typescript-eslint/ban-ts-comment */
/* eslint-disable jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect } from "react";
import { SelectedOption, UserAsset } from "../../../../types";
import UploadDropzone from "../UploadDropzone";
import { User } from "~/common/Store/models/User";
import ImageList from "./ImageList";
import useCustomizableContext from "../../../../useCustomizableContext";
import {
  doesRegionHaveCustomizations,
  getTextBoxes,
} from "~/CustomizableToolPage/fabric/utils";

const CustomizableCanvas = React.lazy(() => import("../CustomizableCanvas"));

interface Props {
  user: User;
  toggleNavigationSidebar: () => void;
}

const ImagePanel = ({ user, toggleNavigationSidebar }: Props) => {
  const {
    // @ts-ignore
    selectedRegion,
    // @ts-ignore
    currentNavigationKey,
    // selectors
    // @ts-ignore
    getNavigationKeyData,
    // @ts-ignore
    getSelectedOptions,
    // @ts-ignore
    getCurrentControls,
    // @ts-ignore
    getSelectedRegionData,
    // @ts-ignore
    getSelectedNavigationMenuOption,
    // thunks
    // @ts-ignore
    addCustomizations,
    // @ts-ignore
    removeCustomizations,
    // @ts-ignore
    selectOption,
    // @ts-ignore
    addUserAssets,
    fabric: { canvas, regions },
  } = useCustomizableContext();

  const showUploadDropzone = !doesRegionHaveCustomizations(
    regions[selectedRegion]
  );

  const navigationKeyData = getNavigationKeyData()[
    currentNavigationKey!.currentNavigationKey
  ];
  const { actualHeight, actualWidth } = navigationKeyData!.regions![
    selectedRegion
  ]!;

  const regionData = getSelectedRegionData();

  const updateValue = useCallback(
    (valueId: string, optionId: number) => {
      // some options are multi region and all regions must be synced to that value
      // @ts-ignore
      const payload: SelectedOption = {
        optionId: navigationKeyData!.id!.toString(),
        attributeId: optionId.toString(),
        regionId: selectedRegion.toString(),
        attributeValueId: Number(valueId),
      };

      selectOption(payload);
    },
    [navigationKeyData, selectOption, selectedRegion]
  );

  const getUserImages = useCallback(
    async (userIndex: number, sessionId: string) => {
      const response = await fetch("/customized-products/user-images/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: userIndex,
          sessionId,
        }),
      });

      if (!response.ok) return;

      const userAssets = await response.json<UserAsset[]>();

      addUserAssets(userAssets);
    },
    [addUserAssets]
  );

  useEffect(() => {
    if (!user.sessionId || !user.index) return;

    getUserImages(Number(user.index), user.sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.sessionId, user.index]);

  useEffect(() => {
    const textBoxes = getTextBoxes(canvas);
    // @ts-ignore
    if (textBoxes.every((tb) => tb.isPlaceholder)) {
      textBoxes.forEach((tb) => canvas.remove(tb));
    }
  }, [canvas]);

  const handleImageAdded = useCallback(
    (target: fabric.Object) => {
      const [key, value] =
        Object.entries(getSelectedOptions(selectedRegion))[0] || [];

      addCustomizations({
        regionId: selectedRegion,
        attributeId: key,
        attributeValueId: value,
        optionId: getSelectedNavigationMenuOption().optionId,
        // @ts-ignore
        assetId: target.assetId,
        // @ts-ignore
        clientIdentifier: target.clientIdentifier,
      });
    },
    [
      addCustomizations,
      getSelectedNavigationMenuOption,
      getSelectedOptions,
      selectedRegion,
    ]
  );

  const handleImageRemoved = useCallback(
    (target: fabric.Object) => {
      const [key, value] =
        Object.entries(getSelectedOptions(selectedRegion))[0] || [];
      // TODO: remove this from customer customized data
      removeCustomizations({
        regionId: selectedRegion,
        attributeId: key,
        attributeValueId: value,
        optionId: getSelectedNavigationMenuOption().optionId,
        // @ts-ignore
        clientIdentifier: target.clientIdentifier,
      });
    },
    [
      getSelectedNavigationMenuOption,
      getSelectedOptions,
      removeCustomizations,
      selectedRegion,
    ]
  );

  const currentControls = getCurrentControls();

  return (
    <>
      <div id="image-panel">
        <span
          className="icon-list customize__menu-icon"
          onClick={toggleNavigationSidebar}
        >
          Options
        </span>
        <div className="customize__img-content">
          <div
            className={`customize__img-text ${
              actualHeight > 0 && actualWidth > 0 && regionData.name.length
                ? ""
                : "hidden"
            }`}
          >
            {`${regionData.name} - Print Area: ${actualHeight}"H X ${actualWidth}"W`}
          </div>
          <CustomizableCanvas
            user={user}
            showUploadDropzone={showUploadDropzone}
            onObjectAdded={handleImageAdded}
            onObjectRemoved={handleImageRemoved}
          />
        </div>

        <div className="form">
          {/* TODO: verify this is correct translation; it's probably not */}
          {currentControls?.map(
            // @ts-ignore
            (option, index) =>
              !(
                index % 2 === 1 && index + 1 <= (currentControls?.length || 0)
              ) && (
                <div
                  className={`${
                    index + 1 >= (currentControls?.length || 0)
                      ? "form__group"
                      : "form__split"
                  }`}
                >
                  {(index + 1 < (currentControls?.length || 0) && (
                    <>
                      <div className="form__group cols-xs--12 cols-sm--6">
                        <div className="form__control-container">
                          <select
                            className="form__control option-select"
                            value={getSelectedOptions()?.[option!.id!] || 0}
                            onChange={({ target: { value } }) =>
                              updateValue(value, option!.id!)
                            }
                          >
                            <option className="form__option" value="0" disabled>
                              Select An Option
                            </option>
                            {/* @ts-ignore */}
                            {Object.values(option.values).map((value) => (
                              <option
                                // @ts-ignore
                                key={value.id}
                                // @ts-ignore
                                value={value.id}
                                className="form__option"
                              >
                                {/* @ts-ignore */}
                                {value.displayValue}
                              </option>
                            ))}
                          </select>
                          <label className="form__label">
                            {option?.attributeName}
                          </label>
                          <span className="icon-down-micro form__control-icon form__control-icon--lg" />
                        </div>
                      </div>

                      <div className="form__group cols-xs--12 cols-sm--6">
                        <div className="form__control-container">
                          <select
                            className="form__control option-select"
                            value={
                              getSelectedOptions()![
                                currentControls![index + 1]!.id!
                              ]
                            }
                            onChange={({ target: { value } }) =>
                              updateValue(
                                value,
                                currentControls![index + 1]!.id!
                              )
                            }
                          >
                            <option className="form__option" value="0" disabled>
                              Select An Option
                            </option>

                            {/* @ts-ignore */}
                            {Object.values(
                              currentControls[index + 1]?.values || []
                            ).map(
                              // @ts-ignore
                              (value) => (
                                <option
                                  className="form__option"
                                  // @ts-ignore
                                  key={value.id}
                                  // @ts-ignore
                                  value={value.id}
                                >
                                  {
                                    // @ts-ignore
                                    value.displayValue
                                  }
                                </option>
                              )
                            )}
                          </select>
                          <label className="form__label">
                            {currentControls![index + 1]!.attributeName}
                          </label>
                          <span className="icon-down-micro form__control-icon form__control-icon--lg" />
                        </div>
                      </div>
                    </>
                  )) || (
                    <div className="form__control-container">
                      <select
                        className="form__control option-select"
                        value={getSelectedOptions()![option!.id!]}
                        onChange={({ target: { value } }) =>
                          updateValue(value, option!.id!)
                        }
                      >
                        <option className="form__option" value="0" disabled>
                          Select An Option
                        </option>
                        {/* @ts-ignore */}
                        {Object.values(option.values).map((value) => (
                          <option
                            // @ts-ignore
                            key={value.id}
                            // @ts-ignore
                            value={value.id}
                            className="form__option"
                          >
                            {/* @ts-ignore */}
                            {value.displayValue}
                          </option>
                        ))}
                      </select>
                      <label className="form__label">
                        {option?.attributeName}
                      </label>
                      <span className="icon-down-micro form__control-icon form__control-icon--lg" />
                    </div>
                  )}
                </div>
              )
          )}
        </div>

        {showUploadDropzone && (
          <div className="customize__footer flex">
            <div className="customize__past-container">
              <ImageList />
            </div>
            <UploadDropzone user={user} />
          </div>
        )}
      </div>
    </>
  );
};

export default ImagePanel;
